import { ClmmPoolUtil, TickMath, type Pool } from '@cetusprotocol/cetus-sui-clmm-sdk';
import WithdrawAmount from '@components/withdraw-amount';
import { useComputeSwapAggregator } from '@hooks/use-compute-swap-aggregator';
import { type CoinStruct } from '@models/coins.vault.model';
import { type MyVaultsStruct } from '@models/myvault.model';
import { scaleDown } from '@utils/math';
import { Segmented } from 'antd';
import { BN, Decimal } from 'parrot-sui-sdk';
import { FC, useState } from 'react';
import { OpenapiClientParrotVault } from 'src/openapi/parrot-vault';
import styles from './index.module.scss';
import WithdrawSubmit from './submit';

interface OwnProps {
  pool: Pool | undefined;
  vault: OpenapiClientParrotVault.GetVaultVaultsResponse['result'][number];
  coinA: CoinStruct;
  coinB: CoinStruct;
  closeDialog(): void;
  myVault: MyVaultsStruct | undefined;
}

const WithdrawVault: FC<OwnProps> = ({ vault, coinA, coinB, pool, closeDialog, myVault }) => {
  const [tab, setTab] = useState(3);

  const [amountA, setAmountA] = useState('');
  const [amountB, setAmountB] = useState('');
  const [exactIn, setExactIn] = useState(true);
  const [percent, setPercent] = useState(0);

  const vaultTabs = [
    {
      label: (
        <div className={`${styles.tab_type} ${tab === 3 ? styles.tab_type_active : ''}`}>
          <img src={coinB?.icon || ''} />
          <span>{coinB?.symbol || ''}</span>
        </div>
      ),
      value: 3,
    },
    {
      label: (
        <div className={`${styles.tab_type} ${tab === 2 ? styles.tab_type_active : ''}`}>
          <img src={coinA?.icon || ''} />
          <span>{coinA?.symbol || ''}</span>
        </div>
      ),
      value: 2,
    },
    {
      label: (
        <div className={`${styles.tab_type} ${tab === 1 ? styles.tab_type_active : ''}`}>
          <img src={coinA?.icon || ''} />
          <span>+</span>
          <img src={coinB?.icon || ''} />
        </div>
      ),
      value: 1,
    },
  ];

  const handleClear = () => {
    setAmountB('');
    setAmountA('');
    setPercent(0);
  };
  const handleTab = (value: number) => {
    setTab(value);
    handleClear();
  };

  const isOnlyCoinA = tab === 2; // only deposit tokenA
  const isOnlyCoinB = tab === 3; // only deposit tokenB

  let balanceA = '0';
  let balanceB = '0';
  if (pool && myVault) {
    const coinAmounts = ClmmPoolUtil.getCoinAmountFromLiquidity(
      new BN(myVault.liquidity),
      new BN(pool.current_sqrt_price),
      TickMath.tickIndexToSqrtPriceX64(vault.tick_lower),
      TickMath.tickIndexToSqrtPriceX64(vault.tick_upper),
      false,
    );
    balanceA = coinAmounts.coinA.toString();
    balanceB = coinAmounts.coinB.toString();
  }

  const { aggregatorRoute } = useComputeSwapAggregator({
    amount: isOnlyCoinA ? balanceB : isOnlyCoinB ? balanceA : '',
    tokenInType: isOnlyCoinA ? coinA.coin_type : coinB.coin_type,
    tokenOutType: isOnlyCoinA ? coinB.coin_type : coinA.coin_type,
    sqrt_price: pool?.current_sqrt_price.toString(),
    a2b: isOnlyCoinA ? true : false,
    lowerIndex: vault.tick_lower,
    upperIndex: vault.tick_upper,
    isRatio: false,
  });

  let availableA = balanceA;
  let availableB = balanceB;
  if (isOnlyCoinA && aggregatorRoute) {
    availableA = new Decimal(balanceA).add(aggregatorRoute.returnAmountWithDecimal).toFixed(0);
    availableB = '0';
  } else if (isOnlyCoinB && aggregatorRoute) {
    availableA = '0';
    availableB = new Decimal(balanceB).add(aggregatorRoute.returnAmountWithDecimal).toFixed(0);
  }

  const percentBalanceA = new Decimal(percent).div(100).mul(balanceA).toFixed(0);
  const percentBalanceB = new Decimal(percent).div(100).mul(balanceB).toFixed(0);
  const { aggregatorRoute: aggregatorResult } = useComputeSwapAggregator({
    amount: isOnlyCoinA ? percentBalanceB : isOnlyCoinB ? percentBalanceA : '',
    tokenInType: isOnlyCoinA ? coinB.coin_type : coinA.coin_type,
    tokenOutType: isOnlyCoinA ? coinA.coin_type : coinB.coin_type,
    sqrt_price: pool?.current_sqrt_price.toString(),
    a2b: isOnlyCoinA ? true : false,
    lowerIndex: vault.tick_lower,
    upperIndex: vault.tick_upper,
    isRatio: false,
  });

  const amount_a = scaleDown(!amountA ? 0 : amountA, coinA.decimals).toString();
  const amount_b = scaleDown(!amountB ? 0 : amountB, coinB.decimals).toString();

  return (
    <div>
      <div className={styles.vault_tabs}>
        <Segmented value={tab} options={vaultTabs} onChange={handleTab} />
      </div>

      <WithdrawAmount
        amountA={amountA}
        amountB={amountB}
        onChangeAmountA={setAmountA}
        onChangeAmountB={setAmountB}
        coinA={coinA}
        coinB={coinB}
        availableA={availableA}
        availableB={availableB}
        tabValue={tab}
        onChangeExactIn={setExactIn}
        onChangePercent={setPercent}
        percent={percent}
        aggregatorRoute={aggregatorRoute}
      />

      <WithdrawSubmit
        myVault={myVault}
        vault={vault}
        amountA={amountA}
        amountB={amountB}
        availableBalanceA={availableA}
        availableBalanceB={availableB}
        tabValue={tab}
        coinA={coinA}
        coinB={coinB}
        closeDialog={closeDialog}
        exactIn={exactIn}
        aggregatorRoute={aggregatorResult}
        percent={percent}
        handleClear={handleClear}
      />

      {aggregatorResult &&
        tab !== 1 &&
        ((isOnlyCoinA && !!amount_a && amount_a !== '0') ||
          (isOnlyCoinB && !!amount_b && amount_b !== '0')) && (
          <div className={styles.vault_swap_info}>
            To remove {isOnlyCoinA ? amount_a : amount_b}{' '}
            {isOnlyCoinA ? coinA.symbol : coinB.symbol}, {aggregatorResult.swapAmount}{' '}
            {isOnlyCoinA ? coinB.symbol : coinA.symbol} in the pool will be traded for{' '}
            {aggregatorResult.returnAmount} {isOnlyCoinA ? coinA.symbol : coinB.symbol}.{' '}
            {/* {aggregatorResult.priceImpact !== null && Number(aggregatorResult.priceImpact) >= 0 && (
              <>
                Price impact:{' '}
                {aggregatorResult.priceImpact > 0.0001
                  ? new Decimal(aggregatorResult.priceImpact).mul(100).toFixed(2) + '%'
                  : '<0.01%'}
              </>
            )} */}
          </div>
        )}
    </div>
  );
};

export default WithdrawVault;
